"use client";

import { createContext, useContext, useState, useEffect } from 'react';
import type { AuthChangeEvent } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import { createCheckout as createCheckoutAction, completeCheckin } from '@/actions/checkout';
import { submitRequest, approveRequestItems, markProjectApproved as markProjectApprovedAction, sendBackForRevision as sendBackForRevisionAction, resolveDeclinedItems as resolveDeclinedItemsAction, markItemPulled as markItemPulledAction, joinPullSheet as joinPullSheetAction, addMissingItemNote as addMissingItemNoteAction, updateRequestTimes as updateRequestTimesAction, cancelRequest as cancelRequestAction, cancelStudentRequest as cancelStudentRequestAction, updateRequest as updateRequestAction, resubmitRequest as resubmitRequestAction, appendItemsToRequest as appendItemsAction, addCollaborator as addCollaboratorAction, removeCollaborator as removeCollaboratorAction, addCartToRequest as addCartToRequestAction, updateRequestItemStaffEdit as updateRequestItemStaffEditAction } from '@/actions/requests';
import { setAcceptOneRevision as setAcceptOneRevisionAction } from '@/actions/requestLeadTimeGates';
import { isPastDue } from '@/lib/dates';
import { authSessionLoad } from '@/lib/perf/authSessionLoad';

type AnyRecord = Record<string, any>;

interface CheckoutContextValue {
  checkouts: AnyRecord[];
  requests: AnyRecord[];
  activeCheckouts: AnyRecord[];
  overdueCheckouts: AnyRecord[];
  pendingRequests: AnyRecord[];
  loading: boolean;
  /** Never throws — returns `{ error }` so Confirm can stay on-step (Next digests / error boundary on thrown Server Actions). */
  addCheckout: (checkout: AnyRecord) => Promise<{ checkout?: AnyRecord; error?: string }>;
  returnCheckout: (
    checkoutId: string,
    itemConditions: Record<string, string>,
    loadInTime?: string,
    receivedQtyMap?: Record<string, number>,
    opts?: {
      checkedInBy?: string | null;
      consumableMeta?: Record<string, { fullnessIn: string; overridden: boolean }>;
    }
  ) => Promise<void>;
  addMissingItemNote: (checkoutId: string, barcode: string, note: string) => Promise<void>;
  addRequest: (request: AnyRecord) => Promise<AnyRecord>;
  updateRequest: (requestId: string, changes: AnyRecord, editNote?: string) => Promise<void>;
  approveRequest: (requestId: string, itemIds: string[]) => Promise<void>;
  declineRequest: (requestId: string, itemIds: string[], note: string) => Promise<void>;
  markProjectApproved: (requestId: string, staffId: string) => Promise<void>;
  sendBackForRevision: (requestId: string, staffId: string, note: string) => Promise<void>;
  setAcceptOneRevision: (requestId: string, enabled: boolean) => Promise<void>;
  resolveDeclinedItems: (
    requestId: string,
    opts: { removeDeclinedIds: string[]; replacements?: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number }[] }
  ) => Promise<void>;
  markItemPulled: (requestId: string, itemId: string, pulled: boolean, staffId?: string | null, staffName?: string | null) => Promise<void>;
  joinPullSheet: (requestId: string, staffId: string) => Promise<void>;
  updateRequestTimes: (requestId: string, pickupTime: string | null, returnTime: string | null) => Promise<void>;
  updateRequestItemStaffEdit: (requestId: string, itemId: string, qty: number, staffNote: string) => Promise<void>;
  cancelRequest: (requestId: string, reason: string, staffName: string) => Promise<void>;
  /** Owner cancel from My Requests — pending/partial/approved/declined until pull/checkout. */
  cancelStudentRequest: (requestId: string, reason?: string) => Promise<void>;
  resubmitRequest: (requestId: string, data: { project: string; projectType: string; isClassAssignment: boolean; notes: string; datesFrom: string; datesTo: string; items: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number; groupId?: string | null; groupLabel?: string | null }[] }) => Promise<void>;
  appendItemsToRequest: (requestId: string, newItems: { barcode: string; itemName: string; qty: number }[]) => Promise<void>;
  addCartToRequest: (requestId: string, items: { barcode: string; itemName: string; qty: number; isWaitlisted?: boolean; variantSelection?: Record<string, number> | null }[], studentId: string) => Promise<void>;
  addCollaborator: (requestId: string, collaboratorId: string) => Promise<void>;
  removeCollaborator: (requestId: string, collaboratorId: string) => Promise<void>;
  getStudentCheckouts: (studentId: string) => AnyRecord[];
  /** includeCollaborations (default true) controls whether requests where this
   * student is only a listed collaborator (not the owner) are included. Staff
   * views (e.g. student detail page) want both; a student's own "My Requests"
   * should be owner-only — a staff-owned request with the student as a
   * collaborator is a hands-on learning moment, not a tracked request in the
   * student's own history. Pass `false` explicitly for student-self views. */
  getStudentRequests: (studentId: string, includeCollaborations?: boolean) => AnyRecord[];
  refresh: () => Promise<void>;
}

let _supabase: ReturnType<typeof createClient> | null = null;
function getSupabase() {
  if (!_supabase) _supabase = createClient();
  return _supabase;
}

// Convert checkout row + checkout_items rows → app camelCase shape
function toCheckout(row: any): AnyRecord {
  return {
    id:                   row.id,
    projectId:            row.project_id ?? null,
    studentId:            row.student_id,
    additionalStudentIds: row.additional_student_ids ?? [],
    staffId:              row.staff_id,
    requestId:            row.request_id ?? null,
    project:              row.project ?? '',
    projectType:          row.project_type ?? '',
    isClassAssignment:    row.is_class_assignment ?? true,
    notes:                row.notes ?? '',
    dueDate:              row.due_date ?? null,
    loadOutTime:          row.load_out_time ?? null,
    loadInTime:           row.load_in_time ?? null,
    source:               row.source ?? 'staff',
    status:               row.status,
    checkedOutAt:         row.checked_out_at ?? row.created_at,
    returnedAt:           row.returned_at ?? null,
    missingItemNotes:     row.missing_item_notes ?? {},
    items: (row.checkout_items ?? []).map((i: any) => ({
      id:              i.id,
      barcode:         i.barcode,
      // 2.5-typed lines (2_5_013_transaction_tables_item_id.sql) are item_id-keyed with
      // barcode usually null — carried through so pull sheet/receipt/check-in can
      // resolve display data via getItems25ByIds() instead of the V1 inventory lookup.
      itemId:          i.item_id ?? null,
      itemName:        i.item_name,
      qty:             i.qty ?? 1,
      category:        i.category ?? null,
      kitType:         i.kit_type ?? null,
      kitId:           i.kit_id ?? null,
      condition:       i.condition_out ?? null,
      returnCondition: i.condition_in ?? null,
      returnedAt:      i.returned_at ?? null,
      receivedQty:     i.received_qty ?? null,
      variantSelection: i.variant_selection ?? null,
      consumableFullnessOut: i.consumable_fullness_out ?? null,
      consumableFullnessIn:  i.consumable_fullness_in ?? null,
      consumableReviewStatus: i.consumable_review_status ?? 'none',
      consumableReviewDueAt: i.consumable_review_due_at ?? null,
      consumableEscalationLevel: i.consumable_escalation_level ?? 0,
      consumableStudentEstimate: i.consumable_student_estimate ?? null,
      consumableOverriddenAtCheckin: i.consumable_overridden_at_checkin ?? false,
      setbackQty:      i.setback_qty ?? 0,
      addedAtCheckout: i.added_at_checkout ?? false,
      groupId:         i.group_id ?? null,
      groupLabel:      i.group_label ?? null,
    })),
  };
}

// Convert request row + request_items rows → app camelCase shape
function toRequest(row: any): AnyRecord {
  return {
    id:               row.id,
    studentId:        row.student_id,
    project:          row.project ?? '',
    projectType:      row.project_type ?? '',
    isClassAssignment: row.is_class_assignment ?? true,
    notes:            row.notes ?? '',
    requestedDates:   { from: row.dates_from ?? null, to: row.dates_to ?? null },
    status:           row.status,
    submittedAt:      row.submitted_at ?? row.created_at,
    updatedAt:        row.updated_at ?? null,
    version:          row.version ?? 1,
    collaboratorIds:  row.collaborator_ids ?? [],
    // Assigned once at approval (markProjectApproved in requests.ts) — stays stable
    // through checkout/return, distinct from this request's own `id` (065_requests_
    // project_id.sql). Unapproved/pending requests have no project id yet.
    projectId:           row.project_id ?? null,
    projectApproved:     row.project_approved ?? false,
    approvedBy:          row.approved_by ?? null,
    needsStudentReview:  row.needs_student_review ?? false,
    approvedVersion:     row.approved_version ?? null,
    acceptOneRevision:   row.accept_one_revision ?? false,
    pulledItems:         row.pulled_items ?? {},
    // Who pulled each item (userId/name/at) + who's currently working this pull sheet —
    // 2026-08-20, multi-staff pull sheet collaboration. mark_item_pulled() RPC writes
    // pulledBy atomically alongside pulledItems; pullParticipants mirrors the existing
    // collaboratorIds shape/pattern above.
    pulledBy:            row.pulled_by ?? {},
    pullParticipants:    row.pull_participants ?? [],
    pickupTime:          row.pickup_time ?? null,
    returnTime:          row.return_time ?? null,
    // Staff-only scratch notes for the pull sheet — separate from `notes` (the
    // student-visible project note, diffed in version_history) — meant to be reviewed
    // and edited day-of-checkout as the plan changes. 2_5_052_request_pull_notes.sql.
    pullNotes:           row.pull_notes ?? '',
    activityLog:         row.activity_log ?? [],
    versionHistory:      row.version_history ?? [],
    requestType:         row.request_type ?? 'student',
    versionNumber:       Math.max(row.version_number ?? 1, row.version ?? 1),
    previousItems:       row.previous_items ?? null,
    lockedAt:            row.locked_at ?? null,
    items: (row.request_items ?? []).map((i: any) => ({
      id:           i.id,
      barcode:      i.barcode,
      // 2.5-typed lines are item_id-keyed with barcode left null (see toCheckout's same
      // note above) — staff checkout's expandReqItems needs this to recognize and carry
      // a 2.5 line through to the checkout cart instead of dropping it as unresolved.
      itemId:       i.item_id ?? null,
      itemName:     i.item_name,
      qty:          i.qty ?? 1,
      status:       i.status ?? 'pending',
      staffNote:    i.staff_note ?? '',
      addedBy:      i.added_by ?? null,
      isWaitlisted: i.is_waitlisted ?? false,
      variantSelection: i.variant_selection ?? null,
      // A 2.5 Set explodes into one row per member at submission (2_5_021, direct user
      // ask for real date-range availability) — rows sharing a groupId came from the
      // same Set, so staff can still recognize them as one kit rather than a
      // disconnected pile of individual item names.
      groupId:      i.group_id ?? null,
      groupLabel:   i.group_label ?? null,
    })),
  };
}

const CheckoutContext = createContext<CheckoutContextValue | null>(null);

export function CheckoutProvider({ children }: { children: React.ReactNode }) {
  const supabase = getSupabase();
  const [checkouts, setCheckouts] = useState<AnyRecord[]>([]);
  const [requests, setRequests] = useState<AnyRecord[]>([]);
  const [loading, setLoading] = useState(true);

  async function fetchAll() {
    setLoading(true);
    try {
      const [{ data: coData }, { data: reqData }] = await Promise.all([
        supabase
          .from('checkouts')
          .select('*, checkout_items(*)')
          .order('created_at', { ascending: false }),
        supabase
          .from('requests')
          .select('*, request_items(*)')
          .order('submitted_at', { ascending: false }),
      ]);
      setCheckouts((coData ?? []).map(toCheckout));
      setRequests((reqData ?? []).map(toRequest));
    } catch (err) {
      if (navigator.onLine) console.error('[CheckoutContext] network error:', err);
    }
    setLoading(false);
  }

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      const decision = authSessionLoad(event, !!session?.user);
      if (decision === 'load') fetchAll();
      if (decision === 'clear') { setCheckouts([]); setRequests([]); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Realtime: push request + checkout changes to all active sessions
  // Requires Realtime enabled on 'requests' and 'checkouts' tables in Supabase
  // (Database → Replication → toggle on)
  //
  // Scoped to the single changed row (with its joined items) instead of a full
  // fetchAll() — a fetchAll() here re-queries the entire checkouts+requests
  // tables on every single insert/update, broadcast to every open session.
  useEffect(() => {
    async function upsertRequest(id: string) {
      const { data } = await supabase.from('requests').select('*, request_items(*)').eq('id', id).single();
      if (!data) return;
      const row = toRequest(data);
      setRequests(prev => prev.some(r => r.id === id) ? prev.map(r => r.id === id ? row : r) : [row, ...prev]);
    }
    async function upsertCheckout(id: string) {
      const { data } = await supabase.from('checkouts').select('*, checkout_items(*)').eq('id', id).single();
      if (!data) return;
      const row = toCheckout(data);
      setCheckouts(prev => prev.some(c => c.id === id) ? prev.map(c => c.id === id ? row : c) : [row, ...prev]);
    }

    // Broadcast handlers (2026-08-21, speed pass — "checkouts and check-ins as well"):
    // a DB trigger (2_5_042_checkout_broadcast_speed.sql) fires realtime.broadcast_changes()
    // on every requests/checkouts insert or update, regardless of which code path wrote
    // it (server action, RPC, direct SQL). That's a direct socket push — tens of ms,
    // no WAL-replication wait — layered ALONGSIDE the postgres_changes handlers below,
    // not replacing them. postgres_changes stays exactly as it was: slower but
    // guaranteed-eventually-consistent off real committed rows, so a dropped broadcast
    // frame (e.g. a socket mid-reconnect) still self-heals a little later instead of
    // silently vanishing. Both funnel into the same upsertRequest/upsertCheckout, which
    // re-fetch by id — applying the same id twice is a harmless no-op.
    function handleBroadcast(msg: any) {
      const row = msg.payload?.record;
      if (!row) return;
      if (msg.payload.table === 'requests') upsertRequest(row.id);
      else if (msg.payload.table === 'checkouts') upsertCheckout(row.id);
    }

    const channel = supabase
      .channel('checkout-data')
      .on('broadcast', { event: 'INSERT' }, handleBroadcast)
      .on('broadcast', { event: 'UPDATE' }, handleBroadcast)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'requests' }, (payload: any) => {
        upsertRequest(payload.new.id);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'requests' }, (payload: any) => {
        upsertRequest(payload.new.id);
      })
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'checkouts' }, (payload: any) => {
        upsertCheckout(payload.new.id);
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'checkouts' }, (payload: any) => {
        upsertCheckout(payload.new.id);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  async function addCheckout(checkout: AnyRecord): Promise<{ checkout?: AnyRecord; error?: string }> {
    const result = await createCheckoutAction({
      studentId:            checkout.studentId,
      additionalStudentIds: checkout.additionalStudentIds ?? [],
      staffId:              checkout.staffId,
      requestId:            checkout.requestId ?? null,
      projectId:            checkout.projectId ?? null,
      project:              checkout.project ?? '',
      projectType:          checkout.projectType ?? '',
      isClassAssignment:    checkout.isClassAssignment ?? true,
      notes:                checkout.notes ?? '',
      dueDate:              checkout.dueDate ?? '',
      // Bug 1 fix (2026-08-25) — per-request-line qty/decline corrections computed by
      // staff/checkout's computeVerifiedSubmission, applied server-side to request_items
      // once the checkout itself commits. Empty/omitted for walk-ins (no request_items).
      requestItemAdjustments: checkout.requestItemAdjustments ?? [],
      items:                checkout.items.map((i: any) => ({
        barcode:      i.barcode ?? null,
        itemId:       i.itemId ?? null,
        itemName:     i.itemName,
        qty:          i.qty ?? 1,
        conditionOut: i.condition ?? null,
        variantSelection: i.variantSelection ?? null,
        addedAtCheckout: i.addedAtCheckout ?? false,
        consumableFullnessOut: i.consumableFullnessOut ?? null,
        setbackQty: i.setbackQty ?? 0,
        groupId: i.groupId ?? i.kitId ?? null,
        groupLabel: i.groupLabel ?? null,
      })),
    });
    if (result.error || !result.id) {
      return { error: result.error || 'Checkout failed. Check your connection and try again.' };
    }
    const id = result.id;
    // Optimistic local record until next refresh. Dedupe against Realtime INSERT —
    // createCheckoutAction commits first, so the postgres_changes handler often
    // upserts the same id before this runs; prepending blindly produced duplicate
    // cards on /staff/checkin (React key warning: two children with the same checkout id).
    const newCheckout = { ...checkout, id, checkedOutAt: new Date().toISOString(), status: 'active', returnedAt: null };
    setCheckouts(prev => prev.some(c => c.id === id)
      ? prev.map(c => c.id === id ? { ...c, ...newCheckout } : c)
      : [newCheckout, ...prev]);
    return { checkout: newCheckout };
  }

  async function returnCheckout(
    checkoutId: string,
    itemConditions: Record<string, string>,
    loadInTime?: string,
    receivedQtyMap?: Record<string, number>,
    opts?: {
      checkedInBy?: string | null;
      consumableMeta?: Record<string, { fullnessIn: string; overridden: boolean }>;
    }
  ) {
    const co = checkouts.find(c => c.id === checkoutId);
    if (!co) return;
    // Keyed by checkout_items.id when present, else itemId/barcode — consumable
    // per-unit lines share an item_id, so the row id is the only unique match key.
    const lineKey = (item: AnyRecord) => item.id ?? item.itemId ?? item.barcode;
    const consumableMeta = opts?.consumableMeta ?? {};
    const items = (co.items ?? [])
      .map((item: AnyRecord) => {
        const key = lineKey(item);
        const meta = consumableMeta[key];
        return {
          key,
          checkoutItemId: item.id ?? null,
          barcode:     item.barcode ?? null,
          itemId:      item.itemId ?? null,
          qty:         receivedQtyMap?.[key] ?? item.qty ?? 1,
          conditionIn: itemConditions[key] ?? item.condition ?? 'Good',
          consumableFullnessIn: (meta?.fullnessIn as any) ?? null,
          consumableOverridden: meta?.overridden ?? false,
        };
      })
      .filter((i: any) => (receivedQtyMap?.[i.key] ?? 0) > 0 || !receivedQtyMap)
      .map(({ key: _key, ...rest }: any) => rest);

    const allReturned = (co.items ?? []).every((item: AnyRecord) => (receivedQtyMap?.[lineKey(item)] ?? 0) >= (item.qty || 1));
    const snapshot = checkouts;
    setCheckouts(prev => prev.map(c => c.id !== checkoutId ? c : {
      ...c,
      status: allReturned ? 'returned' : 'partial-return',
      returnedAt: allReturned ? new Date().toISOString() : c.returnedAt,
    }));
    try {
      await completeCheckin({
        checkoutId,
        items,
        partial: !allReturned,
        checkedInBy: opts?.checkedInBy ?? null,
      });
    } catch (err) {
      setCheckouts(snapshot);
      throw err;
    }
  }

  async function addMissingItemNote(checkoutId: string, barcode: string, note: string) {
    const snapshot = checkouts;
    setCheckouts(prev => prev.map(co =>
      co.id !== checkoutId ? co : { ...co, missingItemNotes: { ...(co.missingItemNotes || {}), [barcode]: note } }
    ));
    try {
      await addMissingItemNoteAction(checkoutId, barcode, note);
    } catch (err) {
      setCheckouts(snapshot);
      throw err;
    }
  }

  async function addRequest(request: AnyRecord): Promise<AnyRecord> {
    const id = await submitRequest({
      studentId:        request.studentId,
      project:          request.project ?? '',
      projectType:      request.projectType ?? '',
      isClassAssignment: request.isClassAssignment ?? true,
      notes:            request.notes ?? '',
      datesFrom:        request.requestedDates?.from ?? '',
      datesTo:          request.requestedDates?.to ?? '',
      items:            (request.items ?? []).map((i: any) => ({
        barcode:  i.barcode,
        itemName: i.itemName,
        qty:      i.qty ?? 1,
        variantSelection: i.variantSelection ?? null,
      })),
      collaboratorIds: request.collaboratorIds ?? [],
    });
    // Fetch fresh from server to avoid duplicate when fetchAll races with optimistic update
    fetchAll();
    return { ...request, id, submittedAt: new Date().toISOString(), status: 'pending' };
  }

  async function updateRequest(requestId: string, changes: AnyRecord, editNote = 'Edited by student') {
    const now = new Date().toISOString();
    // Edit modal historically passed datesFrom/datesTo; the server action expects
    // requestedDates: { from, to }. Normalize here so date edits actually persist.
    const normalized: AnyRecord = { ...changes };
    if (!normalized.requestedDates && (normalized.datesFrom || normalized.datesTo)) {
      normalized.requestedDates = {
        from: normalized.datesFrom ?? null,
        to: normalized.datesTo ?? null,
      };
      delete normalized.datesFrom;
      delete normalized.datesTo;
    }
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const datesChanged = Boolean(
        normalized.requestedDates &&
          (normalized.requestedDates.from !== (req.requestedDates?.from ?? '') ||
            normalized.requestedDates.to !== (req.requestedDates?.to ?? ''))
      );
      const updatedItems = normalized.items
        ? normalized.items.map((i: AnyRecord) => ({ ...i, status: 'pending', staffNote: '' }))
        : datesChanged
          ? req.items.map((i: AnyRecord) => ({ ...i, status: 'pending', staffNote: '' }))
          : req.items;

      // Real before/after diff + version bump, mirroring requests.ts's updateRequest
      // (2026-08-25 fix) — duplicated here rather than shared, since that file is
      // 'use server' and can't export a plain sync helper for client-side use. Keeps
      // the optimistic UI showing the same shape the server will actually persist,
      // instead of the old `{editedAt, note}` placeholder the staff "Student Edits"
      // panel couldn't render anything from.
      const touchesInventoryOrDates = Boolean(normalized.requestedDates || normalized.items);
      let nextVersion = req.version ?? 1;
      let versionHistory = req.versionHistory ?? [];
      if (touchesInventoryOrDates) {
        type DiffItem = { itemId: string | null; barcode: string | null; itemName: string; qty: number };
        const itemKey = (i: DiffItem) => i.itemId ?? i.barcode ?? `name:${i.itemName}`;
        const before: DiffItem[] = (req.items ?? []).map((i: AnyRecord) => ({ itemId: i.itemId ?? null, barcode: i.barcode ?? null, itemName: i.itemName, qty: i.qty ?? 1 }));
        const after: DiffItem[] = normalized.items
          ? normalized.items.map((i: AnyRecord) => ({ itemId: i.itemId ?? null, barcode: i.barcode ?? null, itemName: i.itemName, qty: i.qty ?? 1 }))
          : before;
        const beforeByKey = new Map(before.map((i) => [itemKey(i), i]));
        const afterByKey = new Map(after.map((i) => [itemKey(i), i]));
        let added = 0, removed = 0, changedQty = 0;
        for (const [key, a] of afterByKey) {
          const b = beforeByKey.get(key);
          if (!b) added++;
          else if (b.qty !== a.qty) changedQty++;
        }
        for (const key of beforeByKey.keys()) if (!afterByKey.has(key)) removed++;
        const parts: string[] = [];
        if (added > 0) parts.push(`${added} added`);
        if (removed > 0) parts.push(`${removed} removed`);
        if (changedQty > 0) parts.push(`qty changed on ${changedQty} item${changedQty !== 1 ? 's' : ''}`);
        const summary = normalized.items ? (parts.length > 0 ? parts.join(', ') : 'No item changes') : 'Dates changed';
        nextVersion = Math.max(req.version ?? 1, req.versionNumber ?? 1) + 1;
        versionHistory = [
          ...versionHistory,
          {
            version: nextVersion,
            summary,
            itemsBefore: before,
            itemsAfter: after,
            datesBefore: datesChanged ? req.requestedDates : null,
            datesAfter: datesChanged ? normalized.requestedDates : null,
            changedAt: now,
          },
        ];
      }

      return {
        ...req,
        ...normalized,
        requestedDates: normalized.requestedDates
          ? { from: normalized.requestedDates.from, to: normalized.requestedDates.to }
          : req.requestedDates,
        items: updatedItems,
        status: 'pending',
        projectApproved: datesChanged || normalized.items ? false : req.projectApproved,
        needsStudentReview: datesChanged || normalized.items ? false : req.needsStudentReview,
        updatedAt: now,
        version: nextVersion,
        versionNumber: nextVersion,
        activityLog: [...(req.activityLog ?? []), { action: editNote, at: now }],
        versionHistory,
      };
    }));
    try {
      await updateRequestAction(requestId, normalized, editNote);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  // Keyed by request_items.id, not barcode — Supply items have a null
  // barcode by design, and `.eq('barcode', null)` never matches a row in
  // Postgres, so a barcode-keyed approve/decline silently no-ops for them.
  // id is always present and unique per line item regardless of barcode.
  async function approveRequest(requestId: string, itemIds: string[]) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const updatedItems = req.items.map((i: AnyRecord) => ({ ...i, status: itemIds.includes(i.id) ? 'approved' : i.status }));
      // Line-item approve is scratchpad only — parent stays `partial` (or becomes it)
      // until markProjectApproved. Never optimistic-flip to `approved` here (that
      // desynced staff UI from the server after the 2026-08-09 commit-gate change).
      const anyReviewed = updatedItems.some((i: AnyRecord) => i.status === 'approved' || i.status === 'declined');
      return { ...req, items: updatedItems, status: anyReviewed ? 'partial' : req.status };
    }));
    try {
      await approveRequestItems(requestId, itemIds.map(id => ({ id, status: 'approved' as const })));
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function declineRequest(requestId: string, itemIds: string[], note: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const updatedItems = req.items.map((i: AnyRecord) => ({ ...i, status: itemIds.includes(i.id) ? 'declined' : i.status, staffNote: itemIds.includes(i.id) ? note : i.staffNote }));
      // Same commit-gate as approveRequest — only sendBackForRevision writes parent
      // status='declined'. Mid-review declines leave the request `partial`.
      const anyReviewed = updatedItems.some((i: AnyRecord) => i.status === 'approved' || i.status === 'declined');
      return { ...req, items: updatedItems, status: anyReviewed ? 'partial' : req.status };
    }));
    try {
      await approveRequestItems(requestId, itemIds.map(id => ({ id, status: 'declined' as const, staffNote: note })));
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function markProjectApproved(requestId: string, staffId: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const hasDeclines = (req.items as AnyRecord[]).some((i: AnyRecord) => i.status === 'declined');
      return {
        ...req,
        projectApproved: true,
        status: 'approved',
        needsStudentReview: hasDeclines,
        approvedVersion: req.versionNumber ?? req.version ?? 1,
      };
    }));
    try {
      await markProjectApprovedAction(requestId, staffId);
      await fetchAll();
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function sendBackForRevision(requestId: string, staffId: string, note: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const hasOpenLines = (req.items as AnyRecord[]).some(
        (i) => i.status === 'approved' || i.status === 'pending'
      );
      return {
        ...req,
        status: hasOpenLines ? 'partial' : 'declined',
        projectApproved: false,
        needsStudentReview: true,
        approvedVersion: null,
      };
    }));
    try {
      await sendBackForRevisionAction(requestId, staffId, note);
      await fetchAll();
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function setAcceptOneRevision(requestId: string, enabled: boolean) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : { ...req, acceptOneRevision: enabled }
    ));
    try {
      await setAcceptOneRevisionAction(requestId, enabled);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function resolveDeclinedItems(
    requestId: string,
    opts: { removeDeclinedIds: string[]; replacements?: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number }[] }
  ) {
    const snapshot = requests;
    try {
      await resolveDeclinedItemsAction(requestId, opts);
      await fetchAll();
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function markItemPulled(requestId: string, itemId: string, pulled: boolean, staffId?: string | null, staffName?: string | null) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : {
        ...req,
        pulledItems: { ...(req.pulledItems || {}), [itemId]: pulled },
        pulledBy: pulled && staffId
          ? { ...(req.pulledBy || {}), [itemId]: { userId: staffId, name: staffName ?? 'Staff', at: new Date().toISOString() } }
          : req.pulledBy,
      }
    ));
    try {
      await markItemPulledAction(requestId, itemId, pulled, staffId, staffName);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  // Multi-staff pull sheet collaboration (2026-08-20) — joined when PullSheetModal
  // opens so req.pullParticipants (synced via the existing requests realtime
  // subscription above) shows everyone currently working this request's pull.
  async function joinPullSheet(requestId: string, staffId: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : {
        ...req,
        pullParticipants: req.pullParticipants?.includes(staffId) ? req.pullParticipants : [...(req.pullParticipants ?? []), staffId],
      }
    ));
    try {
      await joinPullSheetAction(requestId, staffId);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function updateRequestTimes(requestId: string, pickupTime: string | null, returnTime: string | null) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : { ...req, pickupTime, returnTime }
    ));
    try {
      await updateRequestTimesAction(requestId, pickupTime, returnTime);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function updateRequestItemStaffEdit(requestId: string, itemId: string, qty: number, staffNote: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      return {
        ...req,
        items: req.items.map((i: AnyRecord) =>
          i.id === itemId ? { ...i, qty, staffNote } : i
        ),
      };
    }));
    try {
      await updateRequestItemStaffEditAction(requestId, itemId, qty, staffNote);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function resubmitRequest(requestId: string, data: { project: string; projectType: string; isClassAssignment: boolean; notes: string; datesFrom: string; datesTo: string; items: { barcode?: string | null; itemId?: string | null; itemName: string; qty: number; groupId?: string | null; groupLabel?: string | null }[] }) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : {
        ...req,
        project: data.project,
        projectType: data.projectType,
        isClassAssignment: data.isClassAssignment,
        notes: data.notes,
        requestedDates: { from: data.datesFrom, to: data.datesTo },
        status: 'pending',
        projectApproved: false,
        needsStudentReview: false,
        version: (req.version ?? 1) + 1,
        updatedAt: new Date().toISOString(),
        items: data.items.map(i => ({
          ...i,
          barcode: i.barcode ?? null,
          itemId: i.itemId ?? null,
          status: 'pending',
          staffNote: '',
        })),
      }
    ));
    try {
      await resubmitRequestAction(requestId, data);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function addCartToRequest(requestId: string, items: { barcode: string; itemName: string; qty: number; isWaitlisted?: boolean; variantSelection?: Record<string, number> | null }[], studentId: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const added = items.map(i => ({ ...i, status: 'pending', staffNote: '' }));
      const allItems = [...req.items, ...added];
      const hasApproved = req.items.some((i: AnyRecord) => i.status === 'approved');
      return {
        ...req,
        status: hasApproved ? 'partial' : 'pending',
        projectApproved: hasApproved ? false : req.projectApproved,
        version: (req.version ?? 1) + 1,
        updatedAt: new Date().toISOString(),
        items: allItems,
      };
    }));
    try {
      await addCartToRequestAction(requestId, items, studentId);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function appendItemsToRequest(requestId: string, newItems: { barcode: string; itemName: string; qty: number }[]) {
    const snapshot = requests;
    setRequests(prev => prev.map(req => {
      if (req.id !== requestId) return req;
      const added = newItems.map(i => ({ ...i, status: 'pending', staffNote: '' }));
      const allItems = [...req.items, ...added];
      const hasApproved = allItems.some((i: AnyRecord) => i.status === 'approved');
      const newStatus = hasApproved ? 'partial' : 'pending';
      return { ...req, items: allItems, status: newStatus, version: (req.version ?? 1) + 1, updatedAt: new Date().toISOString() };
    }));
    try {
      await appendItemsAction(requestId, newItems);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function addCollaborator(requestId: string, collaboratorId: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : {
        ...req,
        collaboratorIds: [...(req.collaboratorIds ?? []), collaboratorId],
      }
    ));
    try {
      await addCollaboratorAction(requestId, collaboratorId);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function removeCollaborator(requestId: string, collaboratorId: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : {
        ...req,
        collaboratorIds: (req.collaboratorIds ?? []).filter((id: string) => id !== collaboratorId),
      }
    ));
    try {
      await removeCollaboratorAction(requestId, collaboratorId);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function cancelRequest(requestId: string, reason: string, staffName: string) {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : { ...req, status: 'cancelled' }
    ));
    try {
      await cancelRequestAction(requestId, reason, staffName);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  async function cancelStudentRequest(requestId: string, reason = '') {
    const snapshot = requests;
    setRequests(prev => prev.map(req =>
      req.id !== requestId ? req : { ...req, status: 'cancelled', needsStudentReview: false }
    ));
    try {
      await cancelStudentRequestAction(requestId, reason);
    } catch (err) {
      setRequests(snapshot);
      throw err;
    }
  }

  // Dedupe by id — Realtime INSERT + optimistic addCheckout used to leave two
  // entries for the same checkout (React duplicate-key warning on /staff/checkin).
  const seenCheckoutIds = new Set<string>();
  const uniqueCheckouts = checkouts.filter(co => {
    if (!co?.id || seenCheckoutIds.has(co.id)) return false;
    seenCheckoutIds.add(co.id);
    return true;
  });
  const activeCheckouts = uniqueCheckouts.filter(co => co.status === 'active' || co.status === 'partial-return' ||
    (co.status === 'overdue') ||
    (co.status === 'active' && co.dueDate && isPastDue(co.dueDate))
  );
  const overdueCheckouts = uniqueCheckouts.filter(co => co.status === 'overdue' || (co.status === 'active' && co.dueDate && isPastDue(co.dueDate)));
  const pendingRequests = requests.filter(r => r.status === 'pending' || r.status === 'partial');

  return (
    <CheckoutContext.Provider value={{
      checkouts: uniqueCheckouts, requests, activeCheckouts, overdueCheckouts, pendingRequests, loading,
      addCheckout, returnCheckout, addMissingItemNote,
      addRequest, approveRequest, declineRequest, markProjectApproved, sendBackForRevision, setAcceptOneRevision, resolveDeclinedItems, markItemPulled, joinPullSheet, updateRequestTimes, updateRequestItemStaffEdit, cancelRequest, cancelStudentRequest, updateRequest, resubmitRequest, appendItemsToRequest, addCartToRequest, addCollaborator, removeCollaborator,
      getStudentCheckouts: (id) => checkouts.filter(co => co.studentId === id),
      getStudentRequests:  (id, includeCollaborations = true) => requests.filter(r =>
        r.studentId === id || (includeCollaborations && (r.collaboratorIds ?? []).includes(id))
      ),
      refresh: fetchAll,
    }}>
      {children}
    </CheckoutContext.Provider>
  );
}

export function useCheckout() {
  const ctx = useContext(CheckoutContext);
  if (!ctx) throw new Error('useCheckout must be used within CheckoutProvider');
  return ctx;
}
